// /src/pages/private/members/rosterTools/utils/rosterHelpers.js

export const ROSTER_FLIP_MS = 380;
export const ROSTER_FLIP_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
export const ROSTER_GHOST_FADE_MS = 260;

export function findRosterParentRowIndex(rows, childIndex) {
  if (!rows[childIndex]?.original?.rosterHandleSecondary) return null;
  for (let j = childIndex - 1; j >= 0; j--) {
    if (!rows[j].original.rosterHandleSecondary) return j;
  }
  return null;
}

export function findParentRowIndexInData(rows, childIndex) {
  if (!rows[childIndex]?.rosterHandleSecondary) return null;
  for (let j = childIndex - 1; j >= 0; j--) {
    if (!rows[j].rosterHandleSecondary) return j;
  }
  return null;
}

export function normalizeRosterStr(s) {
  return (s ?? '').trim().toLowerCase();
}

/** Affiliation column: affiliate membership and/or hasAffiliation → Affiliated. */
export function rosterRowIsAffiliated({ membership, affiliation } = {}) {
  if (normalizeRosterStr(membership) === 'affiliate') return true;
  return affiliation === true;
}

/** Off-org linked slave: not on org roster — always Outsider in affiliation column. */
export function rosterRowIsOutsider(row) {
  if (!row) return false;
  if (row.rosterOffOrgSlave === true) return true;
  return (
    row.rosterHandleSecondary === true &&
    row.payGrade == null &&
    row.rankName == null &&
    row.rosterRank == null &&
    row.tokensRedacted === true
  );
}

/**
 * Identify the viewer's roster row: roster userId, then auth handles, then Discord name.
 */
export function findMyRowInDisplayList(rows, { userId, handles, discordName }) {
  if (!rows?.length) return null;
  const authHandles = (handles || [])
    .map((h) => normalizeRosterStr(h?.handle))
    .filter(Boolean);
  const discordN = normalizeRosterStr(discordName);

  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    if (userId != null && r.userId != null && Number(r.userId) === Number(userId)) {
      return { index: i, row: r };
    }
  }
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const rh = normalizeRosterStr(r.rosterHandle);
    if (rh && authHandles.includes(rh)) {
      return { index: i, row: r };
    }
  }
  if (discordN) {
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      const rd = normalizeRosterStr(r.rosterDiscordName);
      if (rd && rd === discordN) {
        return { index: i, row: r };
      }
    }
  }
  return null;
}

export function rosterRowStableId(row) {
  return `${row.handleId}${row.rosterHandleSecondary ? '-aff' : ''}`;
}

export function monikerFromAuthHandles(handles, rosterHandle) {
  if (!handles?.length || !rosterHandle) return null;
  const rh = normalizeRosterStr(rosterHandle);
  const h = handles.find((x) => normalizeRosterStr(x?.handle) === rh);
  return h?.moniker ?? null;
}

/** L-shape: vertical then a 90° turn with radius r (SVG, y points down). */
export function buildRosterConnectorPath(sx, sy, ex, ey, r = 8) {
  if (ey <= sy) {
    return `M ${sx} ${sy} L ${ex} ${ey}`;
  }
  const vGap = ey - sy;
  const radius = Math.min(r, Math.max(1, vGap / 2));
  const yBeforeArc = ey - radius;
  if (yBeforeArc < sy) {
    const midY = (sy + ey) / 2;
    return `M ${sx} ${sy} L ${sx} ${midY} L ${ex} ${midY} L ${ex} ${ey}`;
  }
  if (ex >= sx + radius) {
    return `M ${sx} ${sy} L ${sx} ${yBeforeArc} A ${radius} ${radius} 0 0 0 ${sx + radius} ${ey} L ${ex} ${ey}`;
  }
  if (ex <= sx - radius) {
    return `M ${sx} ${sy} L ${sx} ${yBeforeArc} A ${radius} ${radius} 0 0 1 ${sx - radius} ${ey} L ${ex} ${ey}`;
  }
  if (ex >= sx) {
    return `M ${sx} ${sy} L ${sx} ${yBeforeArc} A ${radius} ${radius} 0 0 0 ${sx + radius} ${ey} L ${ex} ${ey}`;
  }
  return `M ${sx} ${sy} L ${sx} ${yBeforeArc} A ${radius} ${radius} 0 0 1 ${sx - radius} ${ey} L ${ex} ${ey}`;
}
